/* eslint-disable ts/ban-ts-comment */
import type { FormInst } from 'naive-ui'
import { getCurrentTime, timeFormat, timeStamp } from '@qctsw/utils'
import { APP_ROUTER } from '@qctsw/common'
import type { ReputationParams } from '@/server/types'
import type { OmitPageParam } from '@/composables/usePagination'
import type { CarSelected } from '@/components/Basic/CarSelectFilter/hooks'
import { SERIES_GRADE_ENUM } from '~/shared/types'
import { logicQualityIssues } from '~/logic/common/issue'

export interface PresetTime {
  count: number
  type: 'day' | 'month' | 'year'
  label?: string
}
// 预设时间
export const presetDay: PresetTime[] = [
  { count: 1, type: 'year', label: '最近一年' },
  { count: 30, type: 'day', label: '最近30天' },
  { count: 7, type: 'day', label: '最近7天' },
]

// 车型属性
export const carLevel = [
  { label: '不限', value: 'UNLIMITED' },
  { label: '微型车', value: SERIES_GRADE_ENUM.MINIATURE },
  { label: '小型车', value: SERIES_GRADE_ENUM.MINI },
  { label: '紧凑型车', value: SERIES_GRADE_ENUM.COMPACT },
  { label: '中型车', value: SERIES_GRADE_ENUM.MEDIUM },
  { label: '中大型车', value: SERIES_GRADE_ENUM.MEDIUM_LARGE },
  { label: '大型车', value: SERIES_GRADE_ENUM.LARGE },
  { label: 'SUV', value: SERIES_GRADE_ENUM.SUV },
  { label: 'MPV', value: SERIES_GRADE_ENUM.MPV },
  { label: '其他', value: SERIES_GRADE_ENUM.OTHER },
]

// 动力类型
export const seriesEnergy = [
  { label: '不限', value: undefined },
  { label: '燃油车', value: 'FUEL' },
  { label: '纯电新能源', value: 'EV' },
  { label: '混动新能源', value: 'HEV' },
  { label: '其它车型', value: 'OTHER' },

]

// 厂商分类
export const manufacturer = ['不限', '进口', '合资', '自主'] as const

// 获取某个时间段：eg.七天
export function getRangeTime(count: number, type: 'day' | 'month' | 'year'): [number, number] {
  const today = timeStamp(getCurrentTime()) as number
  const day = timeStamp(today, count, 'subtract', type) as number
  return [day, today]
}

const defaultForm = {
  timeRadio: undefined, // presetDay index
  timePicker: undefined, // [start, end] timestamp
  carBrandId: undefined,
  carBrandInfo: undefined,
  carSeriesId: undefined,
  carSeriesInfo: undefined,
  seriesGrade: undefined,
  productionPlace: undefined,
  issue: undefined,
}

export function useFilter() {
  const route = useRoute()
  // 质量问题选项
  const { qualityIssues, getQualityIssuesHandle } = logicQualityIssues()

  const formRef = ref<FormInst | null>(null)
  const formData = ref<{
    timeRadio: number | undefined
    timePicker?: [number, number]
    carBrandId?: number
    carBrandInfo?: { id: number, name: string }
    carSeriesId?: number
    carSeriesInfo?: { id: number, name: string }
    seriesGrade?: typeof carLevel[number]['value']
    productionPlace?: typeof manufacturer[number]
    issue?: number
  }>({
    timeRadio: undefined, // presetDay index
    timePicker: undefined, // [start, end] timestamp
    carBrandId: undefined,
    carBrandInfo: undefined,
    carSeriesId: undefined,
    carSeriesInfo: undefined,
    seriesGrade: undefined,
    productionPlace: undefined,
    issue: undefined,
  })

  function initRouteQuery() {
    const query = route.query
    if (!query)
      return
    const {
      startTime,
      endTime,
      carBrandId,
      carBrandName,
      carSeriesId,
      carSeriesName,
      seriesGrade,
      productionPlace,
      issue,
    } = query
    if (startTime && endTime && !Number.isNaN(Number(startTime)) && !Number.isNaN(Number(endTime)))
      formData.value.timePicker = [Number(startTime), Number(endTime)] as [number, number]

    if (carBrandId && !Number.isNaN(Number(carBrandId)) && carBrandName) {
      const id = Number(carBrandId)
      formData.value.carBrandId = id
      formData.value.carBrandInfo = { id, name: carBrandName as string }
    }

    if (formData.value.carBrandId && carSeriesName) {
      const id = Number(carSeriesId)
      formData.value.carSeriesId = id
      formData.value.carSeriesInfo = { id, name: carSeriesName as string }
    }
    if (seriesGrade)
      formData.value.seriesGrade = seriesGrade as typeof carLevel[number]['value']
    if (productionPlace)
      formData.value.productionPlace = productionPlace as typeof manufacturer[number]
    if (issue)
      formData.value.issue = Number(issue)
  }

  function carSelected(data: CarSelected) {
    formData.value.carBrandId = data.brand?.id
    formData.value.carBrandInfo = data.brand ? { id: data.brand.id, name: data.brand.brandName } : undefined
    formData.value.carSeriesId = data.series?.id
    formData.value.carSeriesInfo = data.series ? { id: data.series.id, name: data.series.carSeriesName } : undefined
  }

  const filterParams = computed<OmitPageParam<ReputationParams>>(() => {
    const params: OmitPageParam<ReputationParams> = {}

    if (formData.value.timePicker) {
      params.startComplainDate = timeFormat(formData.value.timePicker[0], 'YYYY-MM-DD')
      params.endComplainDate = timeFormat(formData.value.timePicker[1], 'YYYY-MM-DD')
    }
    if (formData.value.carBrandId)
      params.carBrandId = formData.value.carBrandId
    if (formData.value.carSeriesId)
      params.carSeriesId = formData.value.carSeriesId
    if (formData.value.seriesGrade)
      params.seriesGrade = formData.value.seriesGrade === 'UNLIMITED' ? undefined : formData.value.seriesGrade
    if (formData.value.productionPlace)
      params.productionPlace = formData.value.productionPlace
    if (formData.value.issue)
      params.issueId = formData.value.issue

    return params
  })

  const filterRouteQuery = computed(() => ({
    startTime: formData.value.timePicker?.[0],
    endTime: formData.value.timePicker?.[1],
    carBrandId: formData.value.carBrandId,
    carBrandName: formData.value.carBrandInfo?.name,
    carSeriesId: formData.value.carSeriesId,
    carSeriesName: formData.value.carSeriesInfo?.name,
    seriesGrade: formData.value.seriesGrade,
    productionPlace: formData.value.productionPlace,
    issue: formData.value.issue,
  }))

  const pickerChangeShow = ref(true)
  function changePickerShowHandler() {
    pickerChangeShow.value = false
    requestAnimationFrame(() => {
      pickerChangeShow.value = true
    })
  }
  function clearFilter(key?: keyof typeof formData.value) {
    nextTick(() => {
      if (!key) {
        formData.value = { ...defaultForm }
        changePickerShowHandler()
        return
      }
      formData.value[key] = undefined
      if (key === 'timePicker') {
        formData.value.timeRadio = undefined
        changePickerShowHandler()
      }
      if (key === 'carSeriesId')
        formData.value.carSeriesInfo = undefined
      if (key === 'carBrandId') {
        formData.value.carBrandInfo = undefined
        formData.value.carSeriesId = undefined
        formData.value.carSeriesInfo = undefined
      }
    })
  }

  function getShowValue(key: keyof typeof formData.value) {
    if (formData.value[key] === undefined)
      return

    switch (key) {
      case 'timePicker':
        return `${timeFormat(formData.value.timePicker?.[0], 'YYYY/MM/DD')} - ${timeFormat(formData.value.timePicker?.[1], 'YYYY/MM/DD')}`
      case 'carBrandId':
        return formData.value.carBrandInfo?.name
      case 'carSeriesId':
        return formData.value.carSeriesInfo?.name
      case 'seriesGrade':
        return carLevel.find(v => v.value === formData.value.seriesGrade)?.label
      case 'productionPlace':
        // @ts-expect-error
        return `厂商: ${manufacturer[formData.value.productionPlace]}`
      case 'issue':
        return qualityIssues.value.find(v => v.value === formData.value.issue)?.label
    }
  }

  useActivatedWatch(() => [
    watch(() => formData.value.timeRadio, (val) => {
      if (val === undefined)
        return
      const value = presetDay[val]
      formData.value.timePicker = getRangeTime(value.count, value.type) as [number, number]
    }),
    watch(() => formData.value.timePicker, () => {
      if (formData.value.timeRadio === undefined)
        return

      // 选择了预设，但是时间范围选择改变时
      const preset = formData.value.timeRadio
      const presetDates = getRangeTime(presetDay[preset].count, presetDay[preset].type)
      // 当预设时间范围和时间范围选择器的时间不一致时
      if (JSON.stringify(presetDates) !== JSON.stringify(formData.value.timePicker))
        formData.value.timeRadio = undefined
    }),
  ])

  initRouteQuery()
  return {
    formRef,
    formData,
    carSelected,
    // 质量问题选项
    qualityIssues,
    getQualityIssuesHandle,

    clearFilter,
    filterParams,
    filterRouteQuery,
    getShowValue,

    // 修复时间选择器清除后数据未变动
    pickerChangeShow,
  }
}
